import { useGetBriefCampaigns } from "@entities/campaigns/lib/hooks/useGetBriefCampaigns";
import { useGetCampaignFacets } from "@entities/campaigns/lib/hooks/useGetCampaignFacets";
import { useGetCampaignInfluencersFacets } from "@entities/campaigns/lib/hooks/useGetCampaignInfluencersFacets";
import { useGetCampaignPendingReviewActionCount } from "@entities/campaigns/lib/hooks/useGetCampaignPendingReviewActionCount";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { AddCustomFieldsDataButton } from "@features/AddCustomFieldsDataButton";
import { AssignOpsModal } from "@features/assignOpsModal";
import BriefNotifications from "@features/BriefNotifications/BriefNotifications";
import CampaignBriefInfluencerDrawer from "@features/CampaignBriefInfluencerDrawer";
import {
  AttachBriefModal,
  AttachScriptModal,
  ExecutionBulkFooter,
  buildCampaignBulkFilterParams,
  useCampaignExecutionBulk,
} from "@features/CampaignExecutionBulkActions";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { IBriefCampaignInfluencerSearchRequestV2 } from "@shared/lib/types/briefCampains";
import CampaignDraftDrawer from "@widgets/CampaignDraftDrawer";
import { ExecutionStatusFilterEnum } from "@widgets/CampaignExecution/constants";
import {
  CampaignPendingReviewFlag,
  getCampaignPendingReviewChips,
  getCampaignPendingReviewSearchParams,
} from "@widgets/CampaignExecution/lib/campaignExecutionPendingReview";
import { CampaignExecutionPendingReviewBar } from "@widgets/CampaignExecution/ui/CampaignExecutionPendingReviewBar";
import { CampaignExecutionFilters } from "@widgets/CampaignExecution/ui/CampaignExecutionTable/CampaignExecutionFilters";
import { CampaignExecutionTable } from "@widgets/CampaignExecution/ui/CampaignExecutionTable/index";
import CampaignScriptingDrawer from "@widgets/CampaignScriptingDrawer";
import { Flex, Modal } from "antd";
import { Footer } from "antd/es/layout/layout";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import styles from "./styles.module.scss";

export default function BriefCampaignsAll() {
  const { briefId, brief } = useBriefStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  // const [activeStatusFilter, setActiveStatusFilter] =
  //   useState<ExecutionStatusFilter>(ExecutionStatusFilterEnum.All);
  const [activePendingReviewFlag, setActivePendingReviewFlag] =
    useState<CampaignPendingReviewFlag | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const campaignInfluencerId = searchParams.get("campaignInfluencerId");
  const deliverableConfigRefId = searchParams.get("deliverableConfigRefId");
  const taskId = searchParams.get("taskId");
  const drafitngId = searchParams.get("drafitngId");
  const scriptingId = searchParams.get("scriptingId");
  const { userDetails } = useGetMe();

  const [executionDrawerOpen, setExecutionDrawerOpen] = useState(false);
  const [draftDrawerOpen, setDraftDrawerOpen] = useState(false);
  const [scriptingDrawerOpen, setScriptingDrawerOpen] = useState(false);

  const [showPaymentColumns, setShowPaymentColumns] = useState(false);
  const [showDeclinedCreators, setShowDeclinedCreators] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "gantt">("table");
  const [isVendorBillingAttached, setIsVendorBillingAttached] = useState<
    boolean | undefined
  >(undefined);
  const [financeFlags, setFinanceFlags] =
    useState<IBriefCampaignInfluencerSearchRequestV2["financeFlags"]>();
  const [assignedOperationsPoc, setAssignedOperationsPoc] = useState<
    string[] | undefined
  >(undefined);
  const [filters, setFilters] =
    useState<IBriefCampaignInfluencerSearchRequestV2>({
      briefId: briefId || "",
      deliverables: {},
      dynamicFields: {},
      sortByDynamicFields: {},
    });

  const { briefCampaignsFacets } = useGetCampaignFacets(briefId || "");
  const BriefOpsPOCOptions = [
    ...(briefCampaignsFacets?.assignedOperationsPoc || []),
    {
      _id: "unassigned",
      user: {
        _id: "unassigned",
        firstName: "Unassigned",
        lastName: "",
      },
    },
  ];

  const firstRender = useRef(true);

  useEffect(() => {
    if (
      firstRender.current &&
      campaignInfluencerId &&
      deliverableConfigRefId &&
      taskId
    ) {
      if (drafitngId) {
        setDraftDrawerOpen(true);
        firstRender.current = false;
        return;
      }
      if (scriptingId) {
        setScriptingDrawerOpen(true);
        firstRender.current = false;
        return;
      }
      setExecutionDrawerOpen(true);
      firstRender.current = false;
      return;
    }
    return () => {
      firstRender.current = true;
    };
  }, []);

  const { facets, actionPendingOnBrand, anyTaskCompletedCampaignInfluencers } =
    useGetCampaignInfluencersFacets(briefId || "");
  const { pendingReviewCounts } = useGetCampaignPendingReviewActionCount(
    briefId || ""
  );

  const campaignSearchPayload = {
    briefId: briefId || "",
    searchQuery: debouncedSearchQuery || undefined,
    status: ExecutionStatusFilterEnum.All,
    limit: 50,
    isVendorBillingAttached,
    financeFlags,
    assignedOperationsPoc,
    deliverables: filters.deliverables,
    dynamicFields: filters.dynamicFields,
    sortByDynamicFields: filters.sortByDynamicFields,
    ...getCampaignPendingReviewSearchParams(activePendingReviewFlag),
  };

  const {
    flatData,
    pagination,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetBriefCampaigns(
    campaignSearchPayload,
    Boolean(briefId) && viewMode === "table"
  );

  const bulkFilterParams = useMemo(
    () =>
      buildCampaignBulkFilterParams({
        ...campaignSearchPayload,
        // ...(activeStatusFilter === ExecutionStatusFilterEnum.ActionPending
        //   ? { anyTask: { approvalPendingOn: ["Brand"] } }
        //   : activeStatusFilter === ExecutionStatusFilterEnum.Completed
        //     ? { anyLatestTask: { statuses: ["Completed"] } }
        //     : {}),
      } as Record<string, unknown>),
    [
      briefId,
      debouncedSearchQuery,
      // activeStatusFilter,
      isVendorBillingAttached,
      financeFlags,
      assignedOperationsPoc,
      filters.deliverables,
      filters.dynamicFields,
      filters.sortByDynamicFields,
      activePendingReviewFlag,
    ]
  );

  const resetKey = [
    activePendingReviewFlag ?? "",
    String(isVendorBillingAttached),
    JSON.stringify(financeFlags),
    JSON.stringify(assignedOperationsPoc),
    JSON.stringify(filters.deliverables),
    JSON.stringify(filters.dynamicFields),
  ].join("|");

  const bulk = useCampaignExecutionBulk({
    briefId: briefId || "",
    totalCount: pagination?.totalCount,
    filterParams: bulkFilterParams,
    resetKey,
  });

  const visibleCustomFields = useMemo(
    () => brief?.visibileOperationsCustomFieldsV2 || [],
    [brief?.visibileOperationsCustomFieldsV2]
  );

  const attachCreators = useMemo(
    () =>
      (bulk.selectAll
        ? flatData.filter((row) => !bulk.uncheckList.includes(row._id))
        : flatData.filter((row) => bulk.checkedList.includes(row._id))
      ).map((row) => ({
        id: row._id,
        name: row.influencerName,
        image: row.socialMedia?.profilePicUrl || row.influencer?.profilePicUrl,
        deliverableIds: (row.deliverables || []).map(
          (deliverable) => deliverable._id
        ),
      })),
    [flatData, bulk.selectAll, bulk.checkedList, bulk.uncheckList]
  );

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  // const handleStatusFilterChange = useCallback(
  //   (filter: ExecutionStatusFilter) => {
  //     setActiveStatusFilter(filter);
  //     setActivePendingReviewFlag(null);
  //   },
  //   []
  // );

  useEffect(() => {
    if (!activePendingReviewFlag) return;
    const chips = getCampaignPendingReviewChips(pendingReviewCounts?.agency);
    if (!chips.some((chip) => chip.id === activePendingReviewFlag)) {
      setActivePendingReviewFlag(null);
    }
  }, [activePendingReviewFlag, pendingReviewCounts]);

  return (
    <div className={styles.container}>
      <div
        className={`${styles.content} ${
          bulk.hasSelection ? styles.contentWithSelection : ""
        }`}
      >
        <CampaignExecutionTable
          campaignInfluencers={flatData}
          facets={facets}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          title={
            <Flex vertical>
              <div className={styles.controlsBar}>
                <CampaignExecutionFilters
                  actionPendingCount={actionPendingOnBrand}
                  completedCount={anyTaskCompletedCampaignInfluencers}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  // activeStatusFilter={activeStatusFilter}
                  // onStatusFilterChange={handleStatusFilterChange}
                  showPaymentColumns={showPaymentColumns}
                  setShowPaymentColumns={setShowPaymentColumns}
                  showDeclinedCreators={showDeclinedCreators}
                  setShowDeclinedCreators={setShowDeclinedCreators}
                  onAttachBrief={bulk.openAttachBrief}
                  onAttachScript={bulk.openAttachScript}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                />
              </div>
              <div className={styles.pendingReviewWrap}>
                <CampaignExecutionPendingReviewBar
                  counts={pendingReviewCounts?.agency}
                  activeFlag={activePendingReviewFlag}
                  filterParams={campaignSearchPayload}
                  onFlagChange={(flag) => {
                    setActivePendingReviewFlag(flag);
                    bulk.setSelectAll(false);
                    bulk.setCheckedList([]);
                    bulk.setUncheckList([]);
                  }}
                />
              </div>
              {pagination ? (
                <div className={styles.resultsBar}>
                  <span className={styles.tableTitle}>
                    {`${pagination.totalCount} results found${
                      viewMode === "gantt" ? " · Milestone Chart" : ""
                    }`}
                  </span>
                </div>
              ) : null}
            </Flex>
          }
          isLoadingFirstPage={isFetching && flatData.length === 0}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={handleLoadMore}
          activeStatusFilter={ExecutionStatusFilterEnum.All}
          showPaymentColumns={showPaymentColumns}
          isVendorBillingAttached={isVendorBillingAttached}
          setIsVendorBillingAttached={setIsVendorBillingAttached}
          financeFlags={financeFlags}
          setFinanceFlags={setFinanceFlags}
          selectAll={bulk.selectAll}
          checkedList={bulk.checkedList}
          uncheckList={bulk.uncheckList}
          setSelectAll={bulk.setSelectAll}
          setCheckedList={bulk.setCheckedList}
          setUncheckList={bulk.setUncheckList}
          totalCount={pagination?.totalCount}
          assignedOperationsPoc={assignedOperationsPoc}
          setAssignedOperationsPoc={setAssignedOperationsPoc}
          pocOptions={BriefOpsPOCOptions}
          filters={filters}
          setFilters={setFilters}
          hasActiveSearch={Boolean(debouncedSearchQuery?.trim())}
          searchQuery={debouncedSearchQuery}
          activePendingReviewFlag={activePendingReviewFlag}
        />
        <CampaignBriefInfluencerDrawer
          open={executionDrawerOpen}
          onClose={() => {
            setExecutionDrawerOpen(false);
            setSearchParams((next) => {
              next.delete("campaignInfluencerId");
              next.delete("deliverableConfigRefId");
              next.delete("taskId");
              return next;
            });
          }}
          campaignInfluencerId={campaignInfluencerId || ""}
          taskId={taskId || ""}
          deliverableConfigRefId={deliverableConfigRefId || ""}
        />
        <CampaignDraftDrawer
          open={draftDrawerOpen}
          onClose={() => {
            setDraftDrawerOpen(false);
            setSearchParams((next) => {
              next.delete("drafitngId");
              next.delete("campaignInfluencerId");
              next.delete("deliverableConfigRefId");
              next.delete("taskId");
              return next;
            });
          }}
          draftId={drafitngId || ""}
        />
        <CampaignScriptingDrawer
          open={scriptingDrawerOpen}
          onClose={() => {
            setScriptingDrawerOpen(false);
            setSearchParams((next) => {
              next.delete("scriptingId");
              next.delete("campaignInfluencerId");
              next.delete("deliverableConfigRefId");
              next.delete("taskId");
              return next;
            });
          }}
          scriptingId={scriptingId || ""}
        />
        {bulk.hasSelection &&
          createPortal(
            <Footer className={styles.bulkFooter}>
              <ExecutionBulkFooter
                selectedCount={bulk.selectedCount}
                onClear={bulk.clearSelection}
                onAssign={() => bulk.setOpsPocModalProps({ visible: true })}
                onAttachBrief={bulk.openAttachBrief}
                onAttachScript={bulk.openAttachScript}
                extraActions={
                  <AddCustomFieldsDataButton
                    mode="campaign"
                    selectAll={bulk.selectAll}
                    checkedList={bulk.checkedList}
                    uncheckList={bulk.uncheckList}
                    searchPayload={bulkFilterParams}
                    totalCount={pagination?.totalCount || bulk.selectedCount}
                    visibleColumns={visibleCustomFields}
                    onBulkSuccess={bulk.clearSelection}
                  />
                }
              />
            </Footer>,
            document.body
          )}
      </div>

      <Modal
        open={bulk.opsPocModalProps.visible}
        onCancel={() => bulk.setOpsPocModalProps({ visible: false })}
        footer={null}
        title="Assign Ops POC"
        destroyOnClose
      >
        {bulk.opsPocModalProps.visible && (
          <AssignOpsModal
            briefInfluencerId={bulk.checkedList}
            assignedTo={bulk.opsPocModalProps.selectedAssignedTo}
            onConfirm={(_ids, assignedToUserId) =>
              bulk.assignOpsPoc(assignedToUserId)
            }
            confirmLoading={bulk.isBulkActions}
            onCancel={() => bulk.setOpsPocModalProps({ visible: false })}
          />
        )}
      </Modal>

      <AttachBriefModal
        open={bulk.isAttachBriefOpen}
        selectedCount={bulk.selectedCount || pagination?.totalCount || 0}
        creators={attachCreators}
        confirmLoading={bulk.isBulkActions}
        onClose={() => bulk.setIsAttachBriefOpen(false)}
        onConfirm={bulk.attachBrief}
      />
      <AttachScriptModal
        open={bulk.isAttachScriptOpen}
        selectedCount={bulk.selectedCount || pagination?.totalCount || 0}
        creators={attachCreators}
        confirmLoading={bulk.isBulkActions}
        onClose={() => bulk.setIsAttachScriptOpen(false)}
        onConfirm={bulk.attachScript}
      />
      <BriefNotifications
        briefId={briefId || ""}
        selectedUserId={userDetails?.userEntity?.id || ""}
        type="campaignExecution"
      />
    </div>
  );
}
